<template>
    <!-- UApp is mandatory: toasts, tooltips and programmatic overlays do not work without it,
         and it renders the toaster itself — there is no <Toast> to mount. -->
    <UApp :locale="uiLocale" :toaster="{ position: 'top-right' }">
        <!-- The layout renders the page itself (`templates/shell/` Default.vue and Blank.vue each
             carry their own `<router-view />`). Nothing is drawn until the first navigation has
             resolved, or the sign-in page would open with a flash of the signed-in shell — and,
             signed in, until the person is known: the menu and whether this page is theirs at all
             depend on their permissions. -->
        <component :is="layout" v-if="drawn" />

        <!-- Something is being saved: a hairline across the top rather than a curtain over the
             page, so the reviewer keeps reading the scan. -->
        <div
            v-if="appStore.isLoaderActive()"
            class="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-primary/20"
            role="progressbar"
            :aria-label="$t('general.saving')"
        >
            <div class="h-full w-1/3 animate-pulse bg-primary" />
        </div>
    </UApp>
</template>

<script lang="ts" setup>
    import * as locales from '@nuxt/ui/locale'
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { INTL_LOCALE, Locale } from '@/enums/locale'
    import Blank   from '@/layouts/Blank.vue'
    import Default from '@/layouts/Default.vue'
    import { landingRoute, mustChangePassword, routeAllowed } from '@/libs/access'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { IMe } from '@/types/User'

    const route      = useRoute()
    const router     = useRouter()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()
    const { locale } = useI18n()

    // Nuxt UI's own texts (the calendar, "no data") in the reader's language; English is the
    // British variant — day before month in a date field, Monday first.
    const uiLocale = computed(() => locale.value === Locale.En ? locales.en_gb : locales.sl)

    const routerReady = ref(false)
    const meFailed    = ref(false)

    // A failed first navigation still draws the shell rather than an empty window.
    void router.isReady().catch(() => undefined).finally(() => { routerReady.value = true })

    const layouts = { Default, Blank }
    const layout  = computed(() => layouts[route.meta.layout ?? 'Default'])
    const isBlank = computed(() => route.meta.layout === 'Blank')

    /*
     * WHEN THE PAGE MAY BE DRAWN. Signed out, or on the sign-in screen, at once. Signed in, once
     * GET /me has said who this is and the page turned out to be theirs. Should /me fail for any
     * other reason than an expired session, the console is drawn anyway rather than left blank.
     * It latches: once drawn, the shell stays.
     */
    const drawn = ref(false)

    watch(
        () => routerReady.value && (
            !appStore.isLoggedIn()
            || isBlank.value
            || meFailed.value
            || (!!appStore.userData && routeAllowed(route.meta) && (!mustChangePassword() || route.name === 'profile'))
        ),
        (ready) => { if (ready) drawn.value = true },
        { immediate: true },
    )

    /*
     * Who is signed in is asked of the API, once per session. A login answers it itself (`user`
     * in the answer), so this only runs on a reload with a stored token — and a 401 here is the
     * fetch store's ordinary logout.
     */
    const loadMe = async () => {
        meFailed.value = false

        const { statusCode, data } = await fetchStore.fetch('/me').get().json<IMe>()

        if (statusCode.value === 200 && data.value?.id) appStore.setUserData(data.value)
        else if (appStore.isLoggedIn()) meFailed.value = true
    }

    watch(() => appStore.JWT, (jwt) => {
        if (jwt && !appStore.userData) void loadMe()
    }, { immediate: true })

    /*
     * The person is known: a page that is not theirs — opened from an old link, or left behind
     * when an administrator changed their roles — gives way to the landing, and a password
     * somebody else set sends them to the profile to change it. The router's guard makes the same
     * checks on every later navigation.
     */
    watch([() => appStore.userData, () => route.name], ([me]) => {
        if (!me || !route.meta.requiresAuth) return

        if (mustChangePassword() && route.name !== 'profile') void router.replace({ name: 'profile' })
        else if (!routeAllowed(route.meta)) void router.replace(landingRoute())
    })

    watch(locale, (value) => {
        document.documentElement.lang = INTL_LOCALE[value as Locale] ?? value
    }, { immediate: true })
</script>
